import { PalletPrintData } from './PalletPrint'

import { maskUserName } from '@/utils/mask'

// 根据备注文字长度计算字体大小
const getNotesTextStyle = (notes: string): React.CSSProperties => {
  const length = notes.length

  if (length <= 10) {
    return { fontSize: '24px', lineHeight: '1.4' }
  } else if (length <= 20) {
    return { fontSize: '20px', lineHeight: '1.3' }
  } else if (length <= 40) {
    return { fontSize: '16px', lineHeight: '1.2' }
  } else if (length <= 60) {
    return { fontSize: '12px', lineHeight: '1.1' }
  } else {
    return { fontSize: '9px', lineHeight: '1.0' }
  }
}

// 根据单个MARK内容长度计算最适合的字体大小
const getMarkItemStyle = (
  markNumber: string | undefined
): React.CSSProperties => {
  console.log(markNumber)
  const markLength = markNumber?.length || 0
  const baseFontSize = 26

  // 根据单个MARK长度调整字体大小
  let adjustedFontSize = baseFontSize
  if (markLength <= 6) {
    // 短MARK，可以使用更大字体
    adjustedFontSize = Math.min(baseFontSize * 1.2, 16)
  } else if (markLength <= 10) {
    // 中等长度，使用基础字体
    adjustedFontSize = baseFontSize
  } else if (markLength <= 15) {
    // 较长MARK，稍微缩小
    adjustedFontSize = baseFontSize * 0.9
  } else {
    // 很长的MARK，大幅缩小
    adjustedFontSize = baseFontSize * 0.8
  }

  // 计算合适的宽度
  const minWidth = Math.max(12, markLength * 2) + 'mm'
  const maxWidth = '60mm'

  return {
    fontSize: `${Math.max(8, adjustedFontSize)}px`,
    minWidth: minWidth,
    maxWidth: maxWidth,
    padding: '2mm 4mm'
  }
}

// 打印内容组件
const PrintContent: React.FC<{ data: PalletPrintData; qrCodeUrl: string }> = ({
  data,
  qrCodeUrl
}) => (
  <div className="print-container">
    <div className="print-header">
      <div className="print-header-left">パレット仕様書</div>
      {data.printUser && (
        <div className="print-header-right">
          担当者: {maskUserName(data.printUser)}
        </div>
      )}
    </div>

    <div className="print-content">
      <div className="print-info-section">
        {/* 第一行：客户编号和打托日期 */}
        <div className="print-info-row-container">
          <div className="print-info-row-half">
            <div className="print-info-label-small">顧客番号:</div>
            <div className="print-info-value">
              {data.customerNumber || '\u00A0'}
            </div>
          </div>
          <div className="print-info-row-half">
            <div className="print-info-label-small">デバン日:</div>
            <div className="print-info-value">{data.devanDate || '\u00A0'}</div>
          </div>
        </div>

        {/* 第二行：HBL号码独占一行 */}
        <div className="print-info-row-full">
          <div className="print-info-label">HBL番号:</div>
          <div className="print-info-value">
            {data.houseBlNumber || '\u00A0'}
          </div>
        </div>

        {/* 第三行：托盘号码 */}
        <div className="print-info-row-full">
          <div className="print-info-label">パレット:</div>
          <div className="print-info-value">
            {data.palletCode ? (
              <span>
                {data.palletCode.slice(0, -2)}
                <span className="print-pallet-highlight">
                  {data.palletCode.slice(-2)}
                </span>
              </span>
            ) : (
              '\u00A0'
            )}
          </div>
        </div>
      </div>

      {/* 主体内容区域 - 两栏布局 */}
      <div className="print-main-content">
        {/* 左栏：MARK 列表 */}
        <div className="print-marks-section">
          <div className="print-marks-header">
            <div className="print-marks-title">マークの詳細</div>
            <div className="print-marks-stats">
              {(data.cargoMarks?.length || 0) > 1 && (
                <span className="print-stat-item">
                  <span className="print-stat-number pr-1">
                    {data.cargoMarks?.length || 0}
                  </span>
                  点
                </span>
              )}
              <span className="print-stat-item">
                <span className="print-stat-number">
                  {(data.cargoMarks?.length || 0) === 1 && (
                    <>
                      {data.cargoMarks?.[0]?.scannedPackages || 0}
                      <span>{'\uff0f'}</span>
                      {data.cargoMarks?.[0]?.totalBoxes || data.cargoMarks[0]?.totalPackages || 0}
                    </>
                  )}
                  {(data.cargoMarks?.length || 0) > 1 && (
                    <span>{data.total}</span>
                  )}
                </span>
              </span>
            </div>
          </div>

          {(data.cargoMarks?.length || 0) === 1 && (
            <div className="print-marks-list">
              <div
                className="print-mark-item"
                style={getMarkItemStyle(data?.cargoMarks[0]?.cargoNumber)}
              >
                <span>
                  {data?.cargoMarks[0]?.cargoNumber?.slice(0, -4) || ''}
                  <span className="print-mark-highlight">
                    {data?.cargoMarks[0]?.cargoNumber?.slice(-4) || ''}
                  </span>
                </span>
              </div>
            </div>
          )}

          {(data.cargoMarks?.length || 0) > 1 && (
            <div className="print-marks-column">
              {data.cargoMarks?.map((mark, index) => (
                <div key={index} className="print-mark-item-with-dots">
                  <span className="print-mark-name">
                    {mark.cargoNumber || ''}
                  </span>
                  <span className="print-mark-dots"></span>
                  <span className="print-mark-boxes">
                    {mark.totalPackages || 0}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 右栏：二维码 */}
        <div className="print-qr-section">
          {qrCodeUrl && (
            <img src={qrCodeUrl} alt="托盘二维码" className="print-qr-image" />
          )}
        </div>
      </div>

      {/* 底部：备注信息 */}
      <div className="print-notes-section">
        <div className="print-notes-container">
          <div className="print-notes-label">備考</div>
          <div
            className="print-notes-content"
            style={getNotesTextStyle(data.notes || '')}
          >
            {data.notes || ''}
          </div>
        </div>
      </div>
    </div>
  </div>
)

export default PrintContent
